import { DateFormat } from '@/util/enums/date-format';
import moment from 'moment';
import { flow } from 'lodash';
import { DayOfWeekIndex } from '@/util/enums/day-of-week-index';
import { DayOfWeek } from '@/util/types/day-of-week';
import { DAY_OF_WEEK_MAP } from '@/util/constants/day-of-week-map';

/* adding eslint ignore rule per CYBER-6706 */
/* eslint-disable */
const ISOregex = /^(\d{4})-(\d{2})-(\d{2})*$/;
/* eslint-enable */
/**
 * Checks if a string is a valid ISO YYYY-MM-DD string
 *
 * @param str - string to check
 *
 * @returns boolean
 */
export const checkIsValidISOString = (
	str: string,
): boolean => !!str.trim().split(' ')[0].match(ISOregex);

/**
 * Converts an ISO YYYY-MM-DD string to a moment.Moment instance
 *
 * @param ISOString - ISO string to convert
 *
 * @returns moment.Moment instance
 */
export const convertISOStringToMomentInstance = (
	ISOString: string,
): moment.Moment => moment(ISOString, DateFormat['YYYY-MM-DD']);

/**
 * Takes an ISO string, converts it to a Date adds the number of days to it
 * and returns an ISO string representing the new Date
 *
 * @param days - number of days to add
 * @param ISOString - ISO string to add days to
 *
 * @returns ISO string with days added
 */
export const addDaysToISOString = (
	days: number,
	ISOString: string,
): string => {
	if (!checkIsValidISOString(ISOString)) {
		throw new Error('Invalid Date String, must be in format of YYYY-MM-DD');
	}

	return convertISOStringToMomentInstance(ISOString)
		.add(days, 'day')
		.format(DateFormat['YYYY-MM-DD']);
};

/**
 * Converts a Date object to an ISO string YYYY-MM-DD
 *
 * @param date - Date to convert
 *
 * @returns ISO string
 */
export const convertDateToISOString = (
	date: Date,
): string => moment(date).format(DateFormat['YYYY-MM-DD']);

/**
 * Converts a Date object to a UTC ISO string YYYY-MM-DD
 *
 * @param date - Date to convert
 *
 * @returns ISO string
 */
export const convertDateToUTCISOString = (
	date: Date,
): string => moment.utc(date).format(DateFormat['YYYY-MM-DD']);

/**
 * Takes a number and converts it to a percent string
 *
 * @param num number to convert
 * @param decimal amount of decimals places to show after the decimal
 *
 * @returns percent formatted string
 */
export const convertNumberToPercentString = (
	num: number, decimal = 3,
): string => `${(num * 100).toFixed(decimal)}%`;

/**
 * Takes a dollar string value and returns the number equivalent
 * Strips out non numerical characters
 *
 * @param value dollar string
 *
 * @returns number equivalent
 */
export const convertDollarStringToNumber = (
	value: string,
): number => {
	const numRegex = /[^\d.-]/g;
	const formattedAmount = value.replace(numRegex, '');

	try {
		return Number(formattedAmount);
	} catch (err) {
		console.error(err);
		return 0;
	}
};

/**
 * Converts an ISO string YYYY-MM-DD to a Date
 *
 * @param ISOString - ISO string to convert
 *
 * @returns Date
 */
export const convertISOStringToDate = (ISOString: string): Date => {
	if (!checkIsValidISOString(ISOString)) {
		throw new Error('Invalid Date String, must be in format of YYYY-MM-DD');
	}

	return moment(ISOString, DateFormat['YYYY-MM-DD']).toDate();
};

/**
 * Takes a dollar amount as a string or number
 * and adds commas where hundredths exist i.e. xx,xxx,xxx
 *
 * @param dollars dollar value
 *
 * @returns formatted dollar amount string
 */
export const addCommasToDollarAmount = (
	dollars: string | number,
): string =>
	dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * Given a cents dollar or string value
 * Formats the value to staisfy dollar cent value requirements.
 *
 * i.e.
 * If value is 3, should return 30
 * If value is 729 should return 72
 *
 *
 * @param cents cents value
 *
 * @returns formatted cent string
 */
export const formatCentAmount = (
	cents: string | number,
): string => {
	const formattedCents = cents.toString().substring(0, 2);

	if (formattedCents.length === 0) {
		return '00';
	}

	if (formattedCents.length === 1) {
		return `${formattedCents}0`;
	}

	return formattedCents;
};

/**
 * Given a string value
 * Removes all non numerical characters from the string
 *
 * @param value string to evaluate
 *
 * @returns evaluated string
 */
export const removeNonNumbersFromString = (
	value: string,
): string =>
	value.replace(/\D/g, '');

/**
 * Formats a number or string value as a dollar amount
 *
 * @param value - value to be formatted
 * @param prefix - string to prepend to the dollar amount, default = '$'
 *
 * @returns formatted dollar string
 */
export const formatDollar = (
	value: string | number = 0,
	prefix = '$',
): string => {
	const dollarParts = value.toString().split('.');
	const dollars = flow([
		removeNonNumbersFromString,
		addCommasToDollarAmount,
	])(dollarParts[0] || '');

	const cents = flow([
		removeNonNumbersFromString,
		formatCentAmount,
	])(dollarParts[1] || '');

	// Account for negative numbers
	if (Number(value) < 0) {
		return `-${prefix}${dollars}.${cents}`;
	}

	return `${prefix}${dollars}.${cents}`;
};

/**
 * Formats a number or string value as a dollar amount, in place
 *
 * @remarks
 * Generally used on Inputs to format the amount as the user types
 *
 * @param value - dollar amount
 * @param prefix - string to prepend to the dollar amount, default = '$'
 *
 * @returns formatted dollar string
 */
export const convertValueToFormattedDollarStringInPlace = (
	value: string | number = 0,
	prefix = '$',
): string => {
	const dollarValue = value === '.' ? 0 : value;
	const containsDecimal = dollarValue.toString().includes('.');
	const dollarParts = dollarValue.toString().split('.');
	const dollars = flow([
		removeNonNumbersFromString,
		addCommasToDollarAmount,
	])(dollarParts[0] || '');
	const cents = flow([
		removeNonNumbersFromString,
		(v: string) => v.substring(0, 2),
	])(dollarParts[1] || '');

	if (cents.length !== 0) {
		return `${prefix}${dollars}.${cents}`;
	}

	if (containsDecimal) {
		return `${prefix}${dollars}.`;
	}

	return `${prefix}${dollars}`;
};

/**
 * Formats a Date object into a DateFormat based format
 *
 * @param date - Date to format
 * @param format - avialable DateFormat formats
 *
 * @returns formatted string
 */
export const formatDate = (
	date: Date,
	format: DateFormat,
): string => moment(date).format(format);

/**
 * Formats a Date object into a MM/DD/YYYY string
 *
 * @param date - Date to format
 *
 * @returns formatted MM/DD/YYYY string
 */
export const formatDateForDisplay = (
	date: Date | null,
): string => (date ? formatDate(date, DateFormat['MM/DD/YYYY']) : '');

export const formatISOStringForDisplay = (ISOString: string): string => {
	if (!checkIsValidISOString(ISOString)) {
		throw new Error(`Invalid Date String, must be in format of YYYY-MM-DD; recieved: ${ISOString}`);
	}

	const date = convertISOStringToDate(ISOString);

	return formatDateForDisplay(date);
};

/**
 * Formats a value into a formatted phone numer string
 * in the form of (XXX) XXX-XXXX
 *
 * @param phone phone number to format
 * @returns formatted string
 */
export const formatPhoneNumber = (phone: number | string): string => {
	const digits: string = phone.toString().replace(/[^0-9]+/g, '');
	const results = digits.match(/([0-9]{3})([0-9]{3})([0-9]{4})/);

	return results ? `(${results[1]}) ${results[2]}-${results[3]}` : 'N/A';
};

/**
 * Gets the day of the week from a weekday number
 *
 * @param num - day of as number
 *
 * @returns DayOfWeek Object
 */
export const getDayOfWeek = (
	num: DayOfWeekIndex,
): DayOfWeek | null => DAY_OF_WEEK_MAP.get(num) || null;

export const getLastFour = (val: string): string => `${val.substr(-4)}`;

/**
 * Gets the total number of months between 2 dates
 *
 * @param fromDate - start Date
 * @param toDate - end Date
 *
 * @returns number of months
 */
export const getTotalMonthsBetweenDates = (
	fromDate: Date,
	toDate: Date,
): number =>
	Math.abs(
		toDate.getUTCMonth() - fromDate.getUTCMonth() + (12 * (toDate.getUTCFullYear() - fromDate.getUTCFullYear())),
	);

/**
 * Gets the total number of days between 2 dates
 *
 * @param fromDate - start Date
 * @param toDate - end Date
 *
 * @returns number of days
 */
export const getTotalDaysBetweenDates = (
	fromDate: Date,
	toDate: Date,
): number =>
	Math.ceil(
		((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)),
	);

export const ordinal = (value: number): string => {
	if (value === undefined || value === null) {
		return '';
	}

	return moment.localeData().ordinal(value);
};

/**
 * Given a string, replaces all occurences of a specified string
 * with corresponding opening and closing HTML tags
 *
 * @example
 * Replaces all __ with <strong> tags:
 * ```ts
 * const parsedString = parseHTMLTags({
 * 		str: 'The quick __brown__ fox jumped over the __lazy__ __dog__',
 * 		toParse: '__',
 * 		tag: 'strong'
 * })
 * ```
 *
 * @param str - string to parse
 * @param toParse - string to replace with HTML tags
 * @param tag - HTML tag to replace toParse with, can be with brackets or without
 *
 * @returns parsed string with HTML tags
 */
export const parseHTMLTags = (
	{ str, toParse, tag }:
	{ str: string; toParse: string; tag: string },
): string => {
	let parsedString = str;
	const stringParts = parsedString.split(toParse);
	const strippedTag = tag.replace(/[<>/]/g, '');
	const prefixTag = `<${strippedTag}>`;
	const suffixTag = `</${strippedTag}>`;

	if (stringParts.length > 1) {
		let isEven: boolean;
		let isLast: boolean;

		const parsedText = stringParts.map((text, index) => {
			isEven = index % 2 === 0;
			isLast = index === stringParts.length - 1;

			if (isLast) {
				return text;
			}

			if (isEven) {
				return `${text}${prefixTag}`;
			}

			return `${text}${suffixTag}`;
		});

		parsedString = parsedText.join('');
	}

	return parsedString;
};

/**
 * Prepend and appends values to a given value
 *
 * @param append
 * @param prepend
 * @param original
 * @returns evaluated string
 */
export const prependAppend = ({
	append = '',
	original,
	prepend = '',
}: {
	append: string;
	original: string;
	prepend: string;
}): string => `${prepend}${original}${append}`;

/**
 * Given a keyboard event, if the key value is a digit then do nothing
 * else call preventDefault on the event
 *
 * @param event KeyboardEvent
 */
export const restrictKeyboardEventToNumber = (event: KeyboardEvent) => {
	event.key.replace(' ', '');

	if (!/\d/.test(event.key)) {
		event.preventDefault();
	}
};

/**
 * Used with await will force the execution of the loop to wait until the setTimeout resolves
 * based on the milliseconds passed.
 *
 * @param ms milliseconds to sleep for
 */
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Converts a snake_case string to a camelCase string
 *
 * @param str string to convert
 * @returns camelCase string
 */
export const snakeStringToCamel = (str: string): string =>
	str.replace(
		/([_][a-z])/g,
		(group) => group.toUpperCase().replace('_', ''),
	);

/**
 * Converts the property names of an object from snake_case to camelCase
 *
 * @param obj object to convert
 * @returns object with camelCase property names
 */
export const snakeObjectToCamel = (obj: any): any => {
	if (typeof obj !== 'object' || obj === null) {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map((o) => snakeObjectToCamel(o));
	}

	const camelCaseObject: any = {};
	let camelCaseProp: string;

	Object.keys(obj).forEach((prop) => {
		camelCaseProp = snakeStringToCamel(prop);
		camelCaseObject[camelCaseProp] = snakeObjectToCamel(obj[prop as string]);
	});

	return camelCaseObject;
};

/**
 * Given an array of dates, sort the dates by the day of the month
 * Does not take into account any other part of the date
 *
 * @param dates dates to sort
 * @param asc order to sort by
 * @returns sorted dates
 */
export const sortDatesByDayOfMonth = (dates: Date[], asc = true): Date[] =>
	dates.sort(
		(a, b) => (asc
			? a.getDate() - b.getDate()
			: b.getDate() - a.getDate()),
	);

/**
 * Sorts an array of dates
 *
 * @param dates dates to sort
 * @param asc order to sort by
 * @returns sorted dates
 */
export const sortDates = (dates: Date[], asc = true): Date[] =>
	dates.sort(
		(a, b) => (asc
			? a.getTime() - b.getTime()
			: b.getTime() - a.getTime()
		),
	);

/**
 * Given an array of objects with a defined property, as prop, with an ISO string
 * Sorts the array of objects based on the converted date of the ISO string
 *
 * @param asc order to sort by
 * @param objects objects to sort
 * @param prop property on which the ISO string exists
 * @returns sorted array of objects
 */
export const sortObjectsWithISOStrings = (params: {
	asc: boolean;
	objects: any[];
	prop: string;
	}): any[] =>
	[...params.objects].sort((o1, o2) =>
		(params.asc
			? convertISOStringToDate(o1[params.prop]).getTime() - convertISOStringToDate(o2[params.prop]).getTime()
			: convertISOStringToDate(o2[params.prop]).getTime() - convertISOStringToDate(o1[params.prop]).getTime()));
